import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../shared/Button'
import { obByLead, type Onboarding } from './crmApi'

/**
 * LEAD → ONBOARDING SUMMARY (Lee, 30 Oct 2026 — Onboardings module Phase 4).
 * The lead's Onboarding tab is now a light summary: progress per pathway,
 * hours, next session and next steps — with the deep work living in the
 * Onboardings module. One click takes you to this company's page there.
 */
export const ObSummaryTab: React.FC<{ dealId: string }> = ({ dealId }) => {
  const navigate = useNavigate()
  const [ob, setOb] = useState<(Onboarding & Record<string, any>) | null | 'none'>(null)
  useEffect(() => { obByLead(dealId).then((o: any) => setOb(o)).catch(() => setOb('none')) }, [dealId])

  if (ob === null) return <p className="text-[12px] text-txt-tertiary">Loading…</p>
  if (ob === 'none') return <p className="text-[12px] text-txt-tertiary">No onboarding record for this lead yet — it appears when the company signs up or is added on the Onboardings board.</p>

  const stages = (ob.stages || []).filter((s: any) => !s.hidden)
  const done = stages.reduce((a: number, s: any) => a + (s.done || 0), 0)
  const total = stages.reduce((a: number, s: any) => a + (s.total || 0), 0)
  const current = stages[ob.currentStage] || stages.find((s: any) => !s.complete)
  const over = ob.hoursAllowance ? Math.max(0, ob.hoursUsed - ob.hoursAllowance) : 0

  return (
    <div className="max-w-[720px] space-y-3">
      <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-brand-700">Onboarding</p>
          <p className="text-[15px] font-bold text-txt-primary">{ob.plan ? `${ob.plan} plan` : 'Plan awaiting'} · {done}/{total} checkpoints{ob.goLiveDate ? ` · go-live ${new Date(ob.goLiveDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}</p>
        </div>
        <Button size="sm" onClick={() => navigate(`/crm/onboardings/co/${ob.id}`)}>Open in Onboardings →</Button>
      </div>

      <div className="rounded-xl border border-border bg-surface p-3.5">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-txt-tertiary">Progress</p>
          <span className="text-[11px] text-txt-tertiary tabular-nums">{total ? Math.round((done / total) * 100) : 0}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-surface-tertiary overflow-hidden mb-2.5">
          <div className="h-full bg-brand-500" style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }} />
        </div>
        {stages.map((s: any, i: number) => (
          <div key={i} className="flex items-center gap-2 py-0.5">
            <span className={`w-2 h-2 rounded-full shrink-0 ${s.complete ? 'bg-emerald-500' : i === ob.currentStage ? 'bg-brand-500' : 'bg-border-strong'}`} />
            <span className={`text-[11.5px] flex-1 truncate ${i === ob.currentStage ? 'font-bold text-txt-primary' : 'text-txt-secondary'}`}>{s.short || s.name}</span>
            <span className="text-[10.5px] text-txt-tertiary tabular-nums">{s.done}/{s.total}</span>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-2.5">
        <div className="rounded-xl border border-border bg-surface p-3.5">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-txt-tertiary mb-1">Next steps</p>
          {current ? <p className="text-[12.5px] text-txt-primary font-semibold">{current.short || current.name} — {Math.max(0, (current.total || 0) - (current.done || 0))} checkpoint{(current.total || 0) - (current.done || 0) === 1 ? '' : 's'} left</p> : <p className="text-[12px] text-txt-tertiary">All stages complete 🎉</p>}
          {ob.nextMeeting ? (
            <p className="text-[11.5px] text-txt-secondary mt-1">Next session: {new Date(ob.nextMeeting.startAt).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
          ) : <p className="text-[11.5px] text-red-600 font-semibold mt-1">No next session booked</p>}
        </div>
        <div className="rounded-xl border border-border bg-surface p-3.5">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-txt-tertiary mb-1">Hours</p>
          <p className="text-[12.5px] font-bold text-txt-primary">{ob.hoursUsed}h of {ob.hoursAllowance || '—'}h{over > 0 && <span className="text-red-600"> · {over.toFixed(1)}h over</span>}</p>
          {ob.paymentStatus === 'pending' && <p className="text-[11.5px] text-red-600 font-bold mt-1">Payment pending{ob.price ? ` · £${Number(ob.price).toLocaleString('en-GB')}` : ''}</p>}
        </div>
      </div>
    </div>
  )
}
export default ObSummaryTab
